<template>
  <div class="container py-20 px-10">
    <b-loading data-cy="LOADING" isFullPage :active="isLoading" />
    <section>
      <ValidationObserver
        v-if="isAttachment"
        ref="nameValidator"
        class="document-header"
        tag="div"
      >
        <ValidationProvider
          v-slot="{ errors }"
          class="is-flex-grow-1"
          rules="required|max:100"
        >
          <b-field
            :type="
              errors.length
                ? 'is-refused'
                : isContrast
                ? 'is-white'
                : 'is-blue-oil'
            "
          >
            <template #label>
              <div class="is-flex is-flex-direction-row is-align-items-center">
                <div class="custom-pointer" @click="cancel">
                  <b-icon
                    :type="isContrast ? 'is-white' : 'is-blue-oil'"
                    icon="arrow-left-thick"
                    size="is-medium"
                  />
                </div>
                <div class="pl-10">
                  <span
                    class="has-text-weight-semibold has-text-color-7 is-size-4"
                  >
                    {{ General.DOCUMENT }}
                  </span>
                  <p class="is-size-6 has-text-color-7">
                    <span class="has-text-weight-semibold">
                      {{ General.NUP }}:
                    </span>
                    <span class="has-text-weight-normal">
                      {{
                        (currentFile && currentFile.nup) ||
                          General.TEXTS.NUP_NOT_GENERATED
                      }}
                    </span>
                  </p>
                </div>
              </div>
              <p class="has-text-color-7 mt-5">
                {{ General.DOCUMENT_NAME }}
                <span class="optional-label">{{ General.OBLIGATORY }}</span>
              </p>
            </template>
            <b-input
              v-model="attachmentName"
              expanded
              :placeholder="General.PLACEHOLDER.DOCUMENT_NAME"
              :class="{ 'same-line-counter': showInputCounter }"
              :hasCounter="showInputCounter"
              v-bind="isEditable ? { maxlength: 70 } : {}"
            />
            <template #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>
      </ValidationObserver>
      <div v-else class="has-text-weight-semibold is-size-4 ml-20">
        <div class="is-flex is-flex-direction-row is-align-items-center">
          <div class="custom-pointer" @click="cancel">
            <b-icon
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              icon="arrow-left-thick"
              size="is-medium"
            />
          </div>
          <div class="has-text-color-7 pl-10">
            <span class="has-text-weight-semibold is-size-4">
              {{ General.DOCUMENT }}
            </span>
            <p class="is-size-6">
              <span class="has-text-weight-semibold"> {{ General.NUP }}: </span>
              <span class="has-text-weight-normal">
                {{
                  (currentFile && currentFile.nup) ||
                    General.TEXTS.NUP_NOT_GENERATED
                }}
              </span>
            </p>
          </div>
        </div>
        <hr class="mt-5 mb-10" />
      </div>
      <nav class="columns mb-10">
        <div class="column is-narrow is-full-mobile">
          <b-button
            v-if="isMobile"
            outlined
            :type="isContrast ? 'is-white' : 'is-primary'"
            :expanded="isMobile"
            @click="showModalOpenGdocs"
            ><span class="has-text-weight-semibold">
              {{ General.TEXTS.EDIT_IN_DOCS }}
            </span></b-button
          >
        </div>
        <div class="column is-narrow is-full-mobile">
          <b-button
            :type="isContrast ? 'is-white' : 'is-primary'"
            outlined
            :expanded="isMobile"
            @click="showModalModels"
          >
            <span class="has-text-weight-semibold">
              {{ General.DOCUMENT_MODELS }}
            </span>
          </b-button>
        </div>
        <b-tooltip
          v-show="dispatchId"
          class="column is-narrow is-full-mobile"
          :label="General.CLONE_DOCUMENT_DESCRIPTION"
          position="is-right"
          :type="isContrast ? 'is-white' : 'is-blue-oil'"
          size="is-large"
          multilined
        >
          <b-button
            :type="isContrast ? 'is-white' : 'is-primary'"
            outlined
            :expanded="isMobile"
            @click="showModalDocumentClone"
          >
            <span class="has-text-weight-semibold">
              {{ General.CLONE_DOCUMENT }}
            </span>
          </b-button>
        </b-tooltip>
        <b-tooltip
          :active="!(currentFile && currentFile.id)"
          :label="
            attachmentName
              ? General.BLUR_MENSSAGE_AUTOSAVE
              : General.TYPE_DOCUMENT_NAME
          "
          :type="isContrast ? 'is-white' : 'is-blue-oil'"
          size="is-small"
          class="column is-narrow is-full-mobile"
        >
          <b-button
            :type="isContrast ? 'is-white' : 'is-primary'"
            data-cy="DOCUMENT_ATTACH_BUTTON"
            :disabled="!(currentFile && currentFile.id) || isLoading"
            :expanded="isMobile"
            @click="onSubmitDocument"
          >
            <span class="has-text-weight-semibold">
              {{ General.SAVE }}
            </span>
          </b-button>
        </b-tooltip>
      </nav>
    </section>

    <iframe
      v-if="currentFile && currentFile.gdocs_id"
      :key="currentFile.gdocs_id"
      class="gdocs-container"
      title="Janela de edição de documento GDocs"
      :src="
        `https://docs.google.com/document/d/${currentFile.gdocs_id}/edit?usp=sharing&overridemobile=true&rm=embedded`
      "
    ></iframe>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import ModalWarning from "@/views/App/ModalWarning.vue";

import { General } from "@/utils/constants";

import ModalModels from "@/views/App/ModalModels";
import ModalDocumentClone from "@/views/App/ModalDocumentClone";
import { DOCUMENT_TYPE as DOC_TYPE } from "@/utils/enumerate";
import { debounce } from "lodash";

export default {
  name: "DocumentEditor",

  props: {
    id: {
      type: String,
      required: true
    },
    template: {
      type: String,
      default: null
    },
    dispatchId: {
      type: String,
      default: null
    },
    fileId: {
      type: String,
      default: null
    },
    isPrimary: {
      type: Boolean,
      default: false
    },
    isEditable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      General,
      isLoading: false,
      attachmentName: "",
      innerHTML: "",
      content: ``,
      currentFile: {},
      createdFile: false,
      isSaved: false,
      createFileFailed: false
    };
  },

  beforeRouteLeave(_, from, next) {
    if (this.isLoading) return;
    if (!this.isAuthenticated || this.showButton) return next();
    if (!this.isSaved && !this.createFileFailed) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message: `${this.General.MESSAGES.CANCEL_ALERT.QUESTION}<br>${
            this.fileId
              ? this.General.MESSAGES.CANCEL_ALERT.SAVED
              : this.General.MESSAGES.CANCEL_ALERT.NOT_SAVED
          }`,
          confirm: async () => {
            await this.onSubmitDocument(false);
            !this.fileId && (await this.removeFile());
            this.setCurrentFile({});
            next();
          },
          deny: () => {
            next(false);
          },
          buttons: [
            { label: General.YES, isConfirm: true, action: true },
            { label: General.NO, isConfirm: false, action: false }
          ],
          isMessageCentered: true,
          invertOutline: true
        },
        hasModalCard: true,
        trapFocus: true,
        canCancel: false
      });
    } else next();
  },

  computed: {
    ...mapGetters({
      getGdocDocumentAndModels: "getGdocDocumentAndModels",
      getFileTypeById: "getFileTypeById",
      getCurrentFile: "getCurrentFile",
      getProcessFiles: "getProcessFiles",
      getDispatchFiles: "getDispatchFiles",
      getModelsByTypeFiltered: "getModelsByTypeFiltered",
      isContrast: "isContrast",
      isMobile: "isMobile",
      isAuthenticated: "isAuthenticated",
      user: "getCurrentUser"
    }),

    isAttachment() {
      if (!this.template || this.template == "null") return true;
      const fileType = this.getFileTypeById(Number(this.template));
      if (!fileType.attachment_editor) return false;

      return fileType.attachment_editor.includes(1);
    },

    getPrimaryFile() {
      return this.dispatchId
        ? this.getDispatchFiles.find(file => file.primary)
        : this.getProcessFiles.find(file => file.primary);
    },

    disabledSave() {
      return !this.isPrimary && !this.createdFile && !this.fileId;
    },

    disabledCancel() {
      return this.disabledSave && this.attachmentName.length;
    },

    getDocumentId() {
      return (
        this.fileId ||
        this.currentFile?.id ||
        this.getCurrentFile.id ||
        (this.isPrimary && this.getPrimaryFile?.id)
      );
    },

    showInputCounter() {
      return this.attachmentName.length == 70 && this.isEditable;
    }
  },

  watch: {
    attachmentName() {
      if (!this.getDocumentId && !this.isLoading) this.autoSaveDocument();
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await this.fileTypesRequest({
      state: true,
      excludeRequestTypes: !this.isPrimary
    });
    let documentId = this.fileId || this.getCurrentFile.id;

    if (this.isPrimary) {
      if (this.getPrimaryFile?.id) {
        documentId = this.getPrimaryFile?.id;
      }
    }

    if (documentId) {
      await this.setDocumentFile(documentId);
    } else {
      const result = await this.$store.dispatch(
        "getCreateGdocDocumentAndModels",
        {
          processId: this.id,
          documentType: this.template
            ? Number(this.template)
            : DOC_TYPE.ATTACHMENT
        }
      );
      if (!result) {
        this.isSaved = true;
        this.$router.go(-1);
      }
      this.currentFile = this.getGdocDocumentAndModels;

      await this.autoSaveDocument();
    }

    if (this.isPrimary && !documentId) {
      this.isLoading = true;
      await this.saveDocument();
    }
    this.isLoading = false;
  },

  destroyed() {
    this.setCreateGdocDocumentAndModels({});
  },

  methods: {
    ...mapActions({
      editProcessFile: "editProcessFile",
      createDocumentFile: "createDocumentFile",
      primaryFileRequest: "processFilesFilteredRequest",
      deleteProcessFileRequest: "deleteProcessFileRequest",
      fileTypesRequest: "fileTypesRequest",
      getDocumentFile: "getDocumentFile"
    }),

    ...mapMutations({
      setCurrentFile: "setCurrentFile",
      setGoogleUser: "setGoogleUser",
      setCreateGdocDocumentAndModels: "setCreateGdocDocumentAndModels"
    }),

    async saveDocument() {
      this.isLoading = true;
      let documentType = null;
      if (this.fileId)
        await this.deleteProcessFileRequest({ file: { id: this.fileId } });

      if (this.template) {
        documentType = Number(this.template);
      } else if (this.isEditable) {
        documentType = DOC_TYPE.ATTACHMENT;
      }

      const file = {
        process_id: this.id,
        document_type: documentType,
        file: this.innerHTML,
        attachment_name:
          this.getGdocDocumentAndModels.attachment_name || this.attachmentName,
        capacity_id: this.user.capacity?.capacity_id,
        dispatch_id: this.dispatchId || null,
        primary: this.isPrimary,
        editable: this.isEditable,
        gdocs_id: this.currentFile.gdocs_id
      };
      const results = await this.createDocumentFile(file);
      if (results.kind !== "ok") {
        this.createFileFailed = true;
        this.$router.go(-1);
      }
      this.currentFile = results.data;
      this.isLoading = false;

      return results.kind;
    },

    async requestPrimaryFile() {
      await this.primaryFileRequest({
        is_primary: true,
        processId: this.id,
        dispatch_id: this.dispatchId ? this.dispatchId : undefined
      });
    },

    autoSaveDocument: debounce(async function() {
      if (this.fileId) return;
      if (this.attachmentName && (await this.isValidatedNameFile())) {
        this.isLoading = true;
        this.isPrimary && (await this.requestPrimaryFile());
        if (!this.fileId && this.isPrimary && this.getPrimaryFile?.id)
          this.setCurrentFile(this.getPrimaryFile);
        if (this.getDocumentId) {
          await this.editFile();
        } else if (!(this.isAttachment && !this.attachmentName)) {
          await this.saveDocument();
          this.isPrimary && (await this.requestPrimaryFile());
          this.isAttachment && (this.createdFile = true);
        }
        this.isLoading = false;
      }
    }, 1e3),

    async onSubmitDocument(redirect = true) {
      if (await this.isValidatedNameFile()) {
        let status = null;
        if (
          (this.fileId || this.currentFile.id || this.getCurrentFile.id) &&
          !this.getCurrentFile?.status
        ) {
          status = await this.editFile();
        } else status = await this.saveDocument();

        if (status === "ok") {
          this.isSaved = true;
          this.setCurrentFile({});
          redirect && this.$router.back();
        }
      }
    },

    async isValidatedNameFile() {
      return this.isAttachment
        ? await this.$refs.nameValidator.validate()
        : true;
    },

    showModalModels() {
      const currentType = this.getFileTypeById(Number(this.template));
      const isPrimaryDoc = currentType?.attachment_editor?.some(item =>
        [2, 3, 4].includes(item)
      );

      this.$buefy.modal.open({
        parent: this,
        component: ModalModels,
        props: {
          ...(isPrimaryDoc && { defaultDocumentType: this.template }),
          processId: this.id,
          showOnlyPrimaryTypes: !this.isPrimary,
          onSelect: model => this.applyModel(model)
        },
        hasModalCard: true,
        trapFocus: true,
        fullScreen: this.isMobile
      });
    },

    showModalOpenGdocs() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message: General.MESSAGES.WARNING_APP_GOOGLE_DOCUMENTS,
          confirm: () => {
            window.open(
              `https://docs.google.com/document/d/${this.currentFile.gdocs_id}`,
              "_blank"
            );
          },
          buttons: [
            { label: General.CONTINUE, isConfirm: true, action: true },
            { label: General.CANCEL, isConfirm: false, action: false }
          ],
          isMessageCentered: true,
          invertOutline: true,
          iconName: "square-edit-outline"
        },
        hasModalCard: true,
        trapFocus: true,
        canCancel: false
      });
    },

    showModalDocumentClone() {
      const currentType = this.getFileTypeById(Number(this.template));
      const isPrimaryDoc = currentType?.attachment_editor?.some(item =>
        [2, 3, 4].includes(item)
      );

      this.$buefy.modal.open({
        parent: this,
        component: ModalDocumentClone,
        props: {
          ...(isPrimaryDoc && { defaultDocumentType: this.template }),
          processId: this.id,
          isPrimary: this.isPrimary,
          documentId: this.getDocumentId || "",
          onGetDocument: async ({ gdocsId, attachmentName }) => {
            this.attachmentName = this.attachmentName
              ? this.attachmentName
              : attachmentName;
            this.currentFile.gdocs_id = gdocsId;
            await this.autoSaveDocument();
          }
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async setDocumentFile(documentId) {
      await this.getDocumentFile(documentId);
      this.attachmentName = this.getCurrentFile.attachment_name;
      this.content = this.getCurrentFile;

      this.currentFile = this.getCurrentFile;
    },

    applyModel(model) {
      this.currentFile.gdocs_id = model.gdocs_model_id;
    },

    async editFile() {
      this.isLoading = true;
      let newFile;

      if (this.getCurrentFile.id) {
        newFile = this.getCurrentFile;
      } else if (this.isPrimary && this.getPrimaryFile?.id) {
        newFile = this.getPrimaryFile;
      } else {
        newFile = this.currentFile;
      }

      newFile.html_source = this.innerHTML;
      if (this.isAttachment) newFile.attachment_name = this.attachmentName;
      const results = await this.editProcessFile({
        gdoc: { ...newFile, gdocs_id: this.currentFile.gdocs_id }
      });
      this.isLoading = false;
      this.setCurrentFile({});

      return results.kind;
    },

    async removeFile() {
      this.isLoading = true;
      let processFileId =
        this.fileId || this.currentFile.id || this.getCurrentFile.id;
      if (!processFileId && this.isPrimary) {
        await this.requestPrimaryFile();
        processFileId = this.getPrimaryFile?.id;
      }
      processFileId &&
        (await this.deleteProcessFileRequest({ file: { id: processFileId } }));
      this.setCurrentFile({});
      this.isLoading = false;
    },

    cancel() {
      this.$router.back();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.container {
  background-color: $color-1;
}
.document-header {
  display: flex;
  padding: 10px 10px 10px 20px;
  margin-bottom: 10px;
  border-width: 1px;
  border-style: solid;
  border-color: #ccc;
}

.gdocs-container {
  width: 100%;
  height: 700px;
}

.optional-label {
  font-size: 14px;
  font-weight: 400;
  color: $color-7;
  margin-left: 5px;
}
</style>
